import React from 'react';
import { BsX, BsEye, BsPencil, BsPlusCircle, BsShieldLock } from 'react-icons/bs';

interface HelpDrawerCandidatosProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
}

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div style={{ marginBottom: '20px' }}>
    <h4 style={{ fontSize: '14px', fontWeight: '600', color: '#333', marginBottom: '8px', borderBottom: '1px solid #eee', paddingBottom: '6px' }}>
      {title}
    </h4>
    <div style={{ fontSize: '13px', color: '#555', lineHeight: '1.6' }}>{children}</div>
  </div>
);

const ActionItem: React.FC<{ icon: React.ReactNode; label: string; description: string; bgColor: string; borderColor?: string }> = ({ icon, label, description, bgColor, borderColor }) => (
  <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-start', marginBottom: '10px' }}>
    <span style={{
      fontSize: '12px',
      flexShrink: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '28px',
      height: '28px',
      borderRadius: '3px',
      border: `1px solid ${borderColor || '#ccc'}`,
      backgroundColor: bgColor,
    }}>{icon}</span>
    <div>
      <strong>{label}</strong>
      <div style={{ color: '#666', fontSize: '12px' }}>{description}</div>
    </div>
  </div>
);

const DRAWER_WIDTH = 320;

const HelpDrawerCandidatos: React.FC<HelpDrawerCandidatosProps> = ({ isOpen, onClose, children }) => {
  return (
    <>
      <div style={{ marginRight: isOpen ? DRAWER_WIDTH : 0, transition: 'margin-right 0.3s ease' }}>
        {children}
      </div>

      <div style={{
        position: 'fixed',
        top: 0,
        right: isOpen ? 0 : -DRAWER_WIDTH,
        width: DRAWER_WIDTH,
        height: '100vh',
        backgroundColor: '#fff',
        borderLeft: '1px solid #e0e0e0',
        boxShadow: '-2px 0 8px rgba(0,0,0,0.08)',
        transition: 'right 0.3s ease',
        zIndex: 1050,
        display: 'flex',
        flexDirection: 'column',
      }}>
        <div style={{
          padding: '12px 16px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          background: 'linear-gradient(180deg, #2a2d3a 0%, #3c3f4c 100%)',
          color: '#fff',
        }}>
          <h3 style={{ margin: 0, fontSize: '14px', fontWeight: '600' }}>Ajuda — Candidatos</h3>
          <button
            onClick={onClose}
            style={{ background: 'transparent', border: 'none', color: '#fff', fontSize: '18px', cursor: 'pointer', padding: '0 4px' }}
          >
            <BsX />
          </button>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '16px', fontSize: '13px' }}>
          <Section title="Visão Geral">
            <p style={{ margin: '0 0 8px 0' }}>
              Gerencie os candidatos vinculados ao processo. Visualize, edite, crie candidatos e acompanhe o status de cada um.
            </p>
          </Section>

          <Section title="Estatísticas">
            <p style={{ margin: 0 }}>
              O painel no topo mostra o total de candidatos e a distribuição por status: validados, não validados, ausentes, inconclusivos e indefinidos.
            </p>
          </Section>

          <Section title="Filtros">
            <ul style={{ margin: 0, paddingLeft: '18px' }}>
              <li><strong>Nome:</strong> Busca pelo nome do candidato.</li>
              <li><strong>Inscrição:</strong> Número de inscrição no edital.</li>
              <li><strong>CPF:</strong> Documento do candidato.</li>
              <li><strong>Email:</strong> Email de contato.</li>
              <li><strong>Status:</strong> Validado, Não Validado, Ausente, Inconclusivo ou Não Avaliado.</li>
            </ul>
            <p style={{ margin: '8px 0 0 0', fontSize: '12px', color: '#888' }}>
              Filtros aplicados automaticamente. Use "Limpar" para resetar.
            </p>
          </Section>

          <Section title="Ações por Candidato">
            <ActionItem icon={<BsEye style={{ color: '#0d6efd' }} />} label="Visualizar" description="Abre os detalhes completos do candidato." bgColor="white" borderColor="#0d6efd" />
            <ActionItem icon={<BsPencil style={{ color: '#6c757d' }} />} label="Editar" description="Edita dados básicos do candidato (nome, email, etc.)." bgColor="white" borderColor="#6c757d" />
            <ActionItem icon={<BsShieldLock style={{ color: '#dc3545' }} />} label="Dados Sensíveis" description="Solicita alteração de dados sensíveis (CPF, passaporte). Requer aprovação." bgColor="white" borderColor="#dc3545" />
          </Section>

          <Section title="Criar Candidato">
            <ActionItem icon={<BsPlusCircle style={{ color: 'white' }} />} label="Novo Candidato" description="Cadastra um candidato manualmente. Requer permissão 'editar-candidato'." bgColor="#28a745" borderColor="#28a745" />
          </Section>

          <Section title="Status dos Candidatos">
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '12px' }}>
              {[
                { status: 'Não Avaliado', desc: 'Ainda não passou por banca.', color: '#0dcaf0' },
                { status: 'Validado', desc: 'Aprovado pela banca.', color: '#198754' },
                { status: 'Não Validado', desc: 'Reprovado pela banca.', color: '#dc3545' },
                { status: 'Ausente', desc: 'Não compareceu à banca.', color: '#6c757d' },
                { status: 'Inconclusivo', desc: 'Resultado indefinido, pode ir para outra banca.', color: '#ffc107' },
              ].map(({ status, desc, color }) => (
                <div key={status} style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <span style={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: color, flexShrink: 0 }} />
                  <strong style={{ minWidth: '100px' }}>{status}:</strong>
                  <span>{desc}</span>
                </div>
              ))}
            </div>
          </Section>

          <Section title="Dicas">
            <ul style={{ margin: 0, paddingLeft: '18px' }}>
              <li>Candidatos com múltiplas inscrições mostram um badge clicável.</li>
              <li>Todas as modificações são registradas em auditoria.</li>
              <li>Candidatos duplicados são sinalizados automaticamente.</li>
            </ul>
          </Section>
        </div>
      </div>
    </>
  );
};

export default HelpDrawerCandidatos;
